import React, { FC, Fragment, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { GetFeedQueryVariables } from 'api';
import { Loaders } from 'components';
import { useGetFeed, useNotify } from 'hooks';

import { MediumSection, LargeSection, GridSectionTitle } from 'features/feed';

interface Props {
  emptyState: ReactElement;
  variables: GetFeedQueryVariables;
}

const useStyles = makeStyles(theme => ({
  divider: {
    margin: theme.spacing(5, 0, 3, 0),
  },
}));

const GridTimeline: FC<Props> = ({ emptyState, variables }) => {
  const classes = useStyles();
  const notify = useNotify();
  const { t } = useTranslation();
  const { results, isInitialLoad } = useGetFeed(variables, {
    onError: notify.queryError,
  });

  let layout = null;

  if (isInitialLoad) {
    return <Loaders.Feed />;
  }

  function twoResults() {
    return (
      <Fragment>
        <GridSectionTitle title={t('common:featured')} />
        <LargeSection results={results} indexes={[1]} />
      </Fragment>
    );
  }

  function threeResults() {
    return (
      <Fragment>
        <GridSectionTitle title={t('common:featured')} />
        <LargeSection results={results} indexes={[1]} />
        <Divider className={classes.divider} />
        <Box marginTop={5}>
          <LargeSection results={results} indexes={[2]} />
        </Box>
      </Fragment>
    );
  }

  function fourResults() {
    return (
      <Fragment>
        <GridSectionTitle title={t('common:featured')} />
        <LargeSection results={results} indexes={[1]} />
        <Divider className={classes.divider} />
        <Box marginTop={5} paddingBottom={10}>
          <MediumSection results={results} indexes={[2, 3]} />
        </Box>
      </Fragment>
    );
  }

  function fiveResults() {
    return (
      <Fragment>
        <GridSectionTitle title={t('common:featured')} />
        <MediumSection results={results} indexes={[1, 2]} />
        <Box marginTop={10} paddingBottom={10}>
          <MediumSection results={results} indexes={[3, 4]} />
        </Box>
      </Fragment>
    );
  }

  switch (results.length) {
    case 0:
      return emptyState;
    case 1:
      return <LargeSection results={results} indexes={[0]} />;
    case 2:
      layout = twoResults();
      break;
    case 3:
      layout = threeResults();
      break;
    case 4:
      layout = fourResults();
      break;
    case 5:
    default:
      layout = fiveResults();
      break;
  }

  return (
    <Fragment>
      <LargeSection results={results} indexes={[0]} />
      <Divider className={classes.divider} />
      {layout}
    </Fragment>
  );
};

export default GridTimeline;
